// Pure mapping from storefront domain shapes to GA4 ecommerce events. Reads
// only product/price fields — an Order's `contact`, address, voucher code and
// reference never reach a payload (see the allowlist test in map.test.ts).
import type { Money, Order, PricedLine } from "@workspace/model"

import { minorToMajor, toMajor } from "./money.ts"
import type {
  AnalyticsEvent,
  GaItem,
  ItemsEcommerce,
  TrackedLine,
} from "./types.ts"

/** Order states that never turned into a sale. */
const UNSOLD_STATUSES: ReadonlySet<Order["status"]> = new Set([
  "rejected",
  "cancelled",
])

/**
 * Narrows a priced line to its analytics slice. `totalWithExtras` (order reads)
 * OVERRIDES `lineTotal` — see PricedLine — and the line's offer comes off it.
 */
export function trackedLineFromPriced(
  line: PricedLine,
  fallbackName?: string
): TrackedLine {
  const gross = line.totalWithExtras ?? line.lineTotal
  const offer =
    line.offerAmount && line.offerAmount.amount > 0
      ? line.offerAmount
      : undefined
  return {
    itemId: line.itemId,
    name: line.name || fallbackName || line.itemId,
    quantity: line.quantity,
    lineTotal: offer
      ? { amount: gross.amount - offer.amount, currency: gross.currency }
      : gross,
    ...(offer ? { lineDiscount: offer } : {}),
    ...(line.sizeName ? { sizeName: line.sizeName } : {}),
  }
}

/** The same line at another quantity (e.g. the delta of a quantity change). */
export function withQuantity(line: TrackedLine, quantity: number): TrackedLine {
  const scale = (m: Money): Money => ({
    amount: Math.round((m.amount * quantity) / line.quantity),
    currency: m.currency,
  })
  return {
    ...line,
    quantity,
    lineTotal: scale(line.lineTotal),
    ...(line.lineDiscount ? { lineDiscount: scale(line.lineDiscount) } : {}),
  }
}

export function toGaItem(line: TrackedLine, index?: number): GaItem {
  const { currency } = line.lineTotal
  const quantity = Math.max(1, line.quantity)
  const item: GaItem = {
    item_id: line.itemId,
    item_name: line.name,
    price: toMajor(line.lineTotal.amount / quantity, currency),
    quantity: line.quantity,
  }
  if (line.lineDiscount)
    item.discount = toMajor(line.lineDiscount.amount / quantity, currency)
  if (line.sizeName) item.item_variant = line.sizeName
  if (index !== undefined) item.index = index
  return item
}

function itemsEcommerce(
  lines: readonly TrackedLine[],
  fallbackCurrency: string
): ItemsEcommerce {
  const currency = lines[0]?.lineTotal.currency ?? fallbackCurrency
  const minor = lines.reduce((sum, line) => sum + line.lineTotal.amount, 0)
  return {
    currency,
    value: toMajor(minor, currency),
    items: lines.map((line, index) => toGaItem(line, index)),
  }
}

export function viewItemEvent(item: {
  id: string
  name: string
  price: Money
}): AnalyticsEvent {
  return {
    name: "view_item",
    ecommerce: itemsEcommerce(
      [
        {
          itemId: item.id,
          name: item.name || item.id,
          quantity: 1,
          lineTotal: item.price,
        },
      ],
      item.price.currency
    ),
  }
}

export function addToCartEvent(line: TrackedLine): AnalyticsEvent {
  return {
    name: "add_to_cart",
    ecommerce: itemsEcommerce([line], line.lineTotal.currency),
  }
}

export function removeFromCartEvent(
  lines: readonly [TrackedLine, ...TrackedLine[]]
): AnalyticsEvent {
  return {
    name: "remove_from_cart",
    ecommerce: itemsEcommerce(lines, lines[0].lineTotal.currency),
  }
}

export function beginCheckoutEvent(
  lines: readonly TrackedLine[],
  currency: string
): AnalyticsEvent {
  return { name: "begin_checkout", ecommerce: itemsEcommerce(lines, currency) }
}

/**
 * GA4 `purchase` for a placed order, or `null` when the order never became a
 * sale. `value` is `order.total` (a voucher is a tender, not a discount);
 * `tax` is the order's VAT whichever `vatMode` produced it.
 */
export function purchaseEvent(order: Order): AnalyticsEvent | null {
  if (UNSOLD_STATUSES.has(order.status)) return null
  const lines = (order.lines ?? []).map((line) => trackedLineFromPriced(line))
  return {
    name: "purchase",
    ecommerce: {
      transaction_id: order.id,
      currency: order.total.currency,
      value: minorToMajor(order.total),
      items: lines.map((line, index) => toGaItem(line, index)),
      ...(order.vatAmount ? { tax: minorToMajor(order.vatAmount) } : {}),
      ...(order.deliveryFee
        ? { shipping: minorToMajor(order.deliveryFee) }
        : {}),
    },
  }
}
